import { Link } from "react-router-dom";
import { formatCompactNumber, formatRepoName } from "../helpers/helpers";

const Bio = ({ profile, repos, stats }) => {
  const featuredRepos = repos.slice(0, 4);
  const rfc = profile.rfcResearch || {};
  const publications = profile.publications || [];

  return (
    <div className="page resume-page">
      <header className="site-header resume-header">
        <div className="content-width site-header-inner">
          <div className="brand">
            <div className="brand-icon">
              <span className="material-symbols-outlined">description</span>
            </div>
            <span>{profile.brand}</span>
          </div>

          <nav className="site-nav" aria-label="Resume sections">
            <a href="#experience">Experience</a>
            <a href="#publications">Publications</a>
            <a href="#skills">Skills</a>
            <a href="#education">Education</a>
            <a href="#projects">Projects</a>
            <Link to="/" className="resume-chip">
              Home
            </Link>
          </nav>
        </div>
      </header>

      <main className="content-width resume-layout">
        <aside className="resume-sidebar">
          <section className="glass-card resume-profile-card">
            <div className="profile-badge">
              <span className="material-symbols-outlined">person</span>
            </div>
            <h1>{profile.name}</h1>
            <p className="role">{profile.role}</p>

            <div className="profile-meta">
              <p>
                <span className="material-symbols-outlined">location_on</span>
                {profile.location}
              </p>
              <p>
                <span className="material-symbols-outlined">mail</span>
                {profile.email}
              </p>
              <p>
                <span className="material-symbols-outlined">call</span>
                {profile.phone}
              </p>
              <p>
                <span className="material-symbols-outlined">share</span>
                <a href={profile.linkedin} target="_blank" rel="noreferrer">
                  LinkedIn Profile
                </a>
              </p>
              {profile.orcid ? (
                <p>
                  <span className="material-symbols-outlined">badge</span>
                  <a href={profile.orcid} target="_blank" rel="noreferrer">
                    ORCID Profile
                  </a>
                </p>
              ) : null}
              <p>
                <span className="material-symbols-outlined">code</span>
                GitHub: @{profile.githubUser}
              </p>
            </div>

            <div className="profile-actions">
              <a className="btn btn-primary" href={profile.githubUrl} target="_blank" rel="noreferrer">
                GitHub Profile
              </a>
              <a className="btn btn-secondary" href={profile.resumeUrl}>
                Download Resume
              </a>
              <Link className="btn btn-secondary" to="/">
                Portfolio Home
              </Link>
            </div>
          </section>

          <section className="glass-card resume-skill-card">
            <h3>
              <span className="material-symbols-outlined">psychology</span>
              Technical Skills
            </h3>
            <div className="chip-grid compact">
              {profile.stackTools.map((tool) => (
                <span key={tool} className="tool-chip glass-card">
                  {tool}
                </span>
              ))}
            </div>
          </section>
        </aside>

        <section className="resume-content">
          <section className="glass-card panel" id="experience">
            <h2>Experience</h2>
            <p className="panel-subtitle">{profile.bio}</p>
            <div className="timeline">
              {profile.experience.map((item) => (
                <article key={`${item.role}-${item.organization}`} className="timeline-item">
                  <div className="timeline-dot" />
                  <div className="timeline-content">
                    <div className="timeline-head">
                      <div>
                        <h3>{item.role}</h3>
                        <p>{item.organization}</p>
                      </div>
                      <div className="timeline-meta">
                        <span>{item.timeframe}</span>
                        <small>{item.location}</small>
                      </div>
                    </div>
                    <ul>
                      {item.highlights.map((highlight) => (
                        <li key={highlight}>{highlight}</li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="glass-card panel" id="publications">
            <h2>Publications</h2>
            <p className="panel-subtitle">{rfc.status}</p>
            <div className="publication-list">
              {publications.map((publication) => (
                <article key={publication.title} className="publication-item">
                  <div>
                    <p className="eyebrow">{publication.venue} {publication.year}</p>
                    <h3>{publication.title}</h3>
                    <p>{publication.citation}</p>
                    <div className="publication-tags">
                      {publication.subjects?.map((subject) => (
                        <span key={subject}>{subject}</span>
                      ))}
                    </div>
                  </div>
                  <div className="publication-actions">
                    <a className="btn btn-primary" href={publication.href} target="_blank" rel="noreferrer">
                      arXiv
                    </a>
                    <a className="btn btn-secondary" href={publication.pdf} target="_blank" rel="noreferrer">
                      PDF
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="glass-card panel" id="skills">
            <h2>Skills & Expertise</h2>
            <div className="skills-columns">
              {profile.skillGroups.map((group) => (
                <article key={group.title} className="skill-column">
                  <h3>
                    <span className="material-symbols-outlined">{group.icon}</span>
                    {group.title}
                  </h3>
                  <div className="chip-grid compact">
                    {group.items.map((item) => (
                      <span key={item} className="tool-chip glass-card">
                        {item}
                      </span>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="glass-card panel" id="rfc">
            <h2>RFC Research Detail</h2>
            <p className="panel-subtitle">{rfc.arxivId}</p>
            <div className="rfc-panel">
              <h3>{rfc.title}</h3>
              <p>{rfc.summary}</p>
              {rfc.highlights?.length > 0 ? (
                <ul>
                  {rfc.highlights.map((highlight) => (
                    <li key={highlight}>{highlight}</li>
                  ))}
                </ul>
              ) : null}
              <p className="rfc-source">
                Source document: {rfc.source || "data directory manuscript"} / {rfc.arxivId}
              </p>
            </div>
          </section>

          <section className="glass-card panel" id="education">
            <h2>Education & Learning</h2>
            <div className="education-grid">
              {profile.education.map((entry) => (
                <article key={entry.title} className="education-card">
                  <h3>{entry.title}</h3>
                  <p>{entry.institution}</p>
                  <small>{entry.detail}</small>
                </article>
              ))}
            </div>
          </section>

          <section className="glass-card panel" id="projects">
            <h2>Project Snapshot</h2>
            <p className="panel-subtitle">
              Live GitHub totals: {formatCompactNumber(stats.stars)} stars, {formatCompactNumber(stats.forks)} forks.
            </p>

            {featuredRepos.length > 0 ? (
              <div className="repo-list">
                {featuredRepos.map((repo) => (
                  <a key={repo.id} href={repo.html_url} target="_blank" rel="noreferrer" className="repo-list-item">
                    <div>
                      <h3>{formatRepoName(repo.displayName || repo.name)}</h3>
                      <p>{repo.description}</p>
                    </div>
                    <span>
                      Stars: {formatCompactNumber(repo.stargazers_count)} | Forks: {formatCompactNumber(repo.forks_count)}
                    </span>
                  </a>
                ))}
              </div>
            ) : (
              <p className="empty-message">Repository data will appear here once loaded.</p>
            )}
          </section>
        </section>
      </main>

      <footer className="site-footer resume-footer">
        <div className="content-width">
          <p>Resume format inspired by the stitched templates and populated with live portfolio data.</p>
        </div>
      </footer>
    </div>
  );
};

export default Bio;
